import videojs from 'video.js';

import './modal-dialog.css';

const _allModals = [];
const _displayAsPanel = new WeakMap();

export default class ModalDialog extends videojs.getComponent('ModalDialog') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {Object} options
     * @param {string} options.text
     * @param {OVERLAY_POSITION} [options.position]
     * @param {string} [options.className]
     * @param {boolean} [options.displayAsPanel]
     * @param {boolean} [options.solidBackground]
     */
    constructor(player, options) {
        super(player, options);
        this.addClass('mediasite-player__modal-dialog');
        this.displayAsPanel = options.displayAsPanel || false;
        this.solidBackground = options.solidBackground ?? true;
        _allModals.push(this);
    }

    open(...args) {
        super.open(...args);

        this.one('animationend', () => {
            this.el().querySelector('input')?.focus();
        });

        if (this.displayAsPanel) {
            const existingModal = _allModals.find(modal => modal.displayAsPanel && modal.opened() && modal !== this);
            existingModal?.close();
        }

        this.triggerLayoutUpdate();

        this.trigger('modalopened');
    }

    close(...args) {
        if (super.opened()) {
            super.close(...args);
            const exitClass = 'mediasite-player__modal-dialog--exiting';
            try {
                this.addClass(exitClass);
                this.one('animationend', () => {
                    this.removeClass(exitClass);
                    this.triggerLayoutUpdate();
                });
            }
            catch (e) { }
        }
    }

    triggerLayoutUpdate() {
        if (_allModals.some(modal => modal.displayAsPanel && modal.opened())) {
            this.player().addClass('mediasite-player--sidebar-open');
            this.player().controls(true);
        }
        else {
            this.player().removeClass('mediasite-player--sidebar-open');
        }
        this.player().trigger('playerresize');
    }

    get displayAsPanel() {
        return _displayAsPanel.get(this);
    }

    set displayAsPanel(displayAsPanel) {
        _displayAsPanel.set(this, displayAsPanel);

        if (displayAsPanel) {
            this.addClass('mediasite-player__modal-dialog--panel');
        }
        else {
            this.removeClass('mediasite-player__modal-dialog--panel');
        }

        this.triggerLayoutUpdate();
    }

    get solidBackground() {
        return this.hasClass('mediasite-player__modal-dialog--solid-background');
    }

    set solidBackground(solidBackground) {
        if (solidBackground) {
            this.addClass('mediasite-player__modal-dialog--solid-background');
        }
        else {
            this.removeClass('mediasite-player__modal-dialog--solid-background');
        }
    }

    /**
     * This is how the official Video.js "TextTrackSettings" component resets focus to the captions button on close.
     * I don't like how it's labeled in the Video.js source code as a private function, but it appears to be the
     * only way to do this accessibly without reimplementing more of the modal component.
     * @see {@link https://github.com/videojs/video.js/blob/main/src/js/tracks/text-track-settings.js#L589}
     * @private
     */
    conditionalBlur_() {
        if (this.previouslyActiveEl_ && this.previouslyActiveEl_.classList.contains('vjs-menu-item')) {
            // ModalDialog component automatically focuses this.previouslyActiveEl_ on close; setting it here lets us override which button gets focused
            this.previouslyActiveEl_ = this.previouslyActiveEl_.closest('.vjs-menu-button').querySelector('.vjs-menu-button');
        }
        super.conditionalBlur_();
    }

    dispose() {
        super.dispose();
        const indexInArray = _allModals.indexOf(this);
        if (indexInArray > -1) {
            _allModals.splice(indexInArray, 1);
        }
    }
}
